import { useMemo } from 'react'

import { addDays, format } from 'date-fns'
import { isBoolean } from 'lodash'
import _find from 'lodash/find'

import { ProfileTypes } from '@/constants/applicationProfile'
import { DOCUMENT_OPTIONS } from '@/constants/documents'
import { escalationFlagsTypes } from '@/constants/escalations'
import { checkWithinThreshold } from '@/features/DealSummary/CreditReportSection/CreditSummaryMatrix'
import useDocuments from '@/features/FileManager/useDocuments'
import {
  appraisalReviewStatusOptions,
  conditionOfAppraisalTypeOptions,
  escrowStatusTypeOptions,
  maritalStatusOptions,
  payoffTypeOptions,
  propertyTypeOptions,
  propertyUsageOptions,
  taxesOptions,
  useOfProceedsOptions,
} from '@/features/application/applicationSlice'
import { Application } from '@/graphql/generated'
import {
  useGetApplicationCalcsQuery,
  useGetApplicationOfferCalcsQuery,
  useGetApplicationValuationsCalcsQuery,
} from '@/graphql/services/applicationCalcs'
import { useGetAuditCheckSummaryQuery } from '@/services/application'
import { useGetDocumentMetadataQuery } from '@/services/documents'
import { useGetEscalationsQuery } from '@/services/escalations'
import { useListHOIQuery } from '@/services/hoi'
import { useGetOverrideByApplicationQuery } from '@/services/override'
import { useGetLiensQuery, useGetPayoffsQuery } from '@/services/tradelines'
import { useListUseOfProceedsQuery } from '@/services/useOfProceeds'
import {
  useListApplicationProfilesQuery,
  useListNonApplicantsQuery,
} from '@/services/v2-application/applicationProfile'
import { useGetAtypicalitCheckCalcsQuery } from '@/services/v2-application/atypicalityCheck'
import { useGetValuationsByApplicationQuery } from '@/services/v2-application/valuation'
import { getApplicationProfileByPosition } from '@/utils/applicationProfile'
import { useCreditReports } from '@/utils/creditReport'

const noContent = 'NA'

const yesOrNo = (condition: boolean) => (condition ? 'Yes' : 'No')

export const useDealNotesTemplates = (application: Application, notes) => {
  const { data: valuationsCalcsResponse } = useGetApplicationValuationsCalcsQuery(
    {
      externalId: application?.externalId,
    },
    { skip: !application?.externalId }
  )
  const valuationsCalcs = useMemo(
    () => valuationsCalcsResponse?.application?.valuationsCalculations,
    [valuationsCalcsResponse]
  )

  const { data: applicationOfferCalcsResponse } = useGetApplicationOfferCalcsQuery(
    { externalId: application?.externalId },
    {
      skip: !application?.externalId,
    }
  )
  const applicationOfferCalcs = useMemo(
    () => applicationOfferCalcsResponse?.application?.offerCalculations,
    [applicationOfferCalcsResponse]
  )

  const { data: applicationCalcsResponse } = useGetApplicationCalcsQuery(
    { externalId: application?.externalId },
    {
      skip: !application?.externalId,
    }
  )

  const applicationCalcs = useMemo(
    () => applicationCalcsResponse?.application?.applicationCalculations,
    [applicationCalcsResponse]
  )

  const { data: escalations } = useGetEscalationsQuery(application?.externalId, {
    skip: !application?.externalId,
  })

  const { data: overrides } = useGetOverrideByApplicationQuery(application?.uuid, {
    skip: !application,
  })

  const { data: hoi } = useListHOIQuery(application?.uuid, { skip: !application?.uuid })

  const { sortedCreditReports, realCreditReport } = useCreditReports(
    application?.uuid,
    application?.creditReports
  )

  const { data: applicationProfiles } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const { data: useOfProceeds } = useListUseOfProceedsQuery(application?.id, {
    skip: !application?.id,
  })

  const { data: atypicalityCheckCalcs } = useGetAtypicalitCheckCalcsQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const { data: globalAudit } = useGetAuditCheckSummaryQuery(application?.externalId, {
    skip: !application?.externalId,
  })

  const { data: documents } = useDocuments()

  const mortageStatement = useMemo(() => {
    return documents?.find((doc) =>
      doc?.fileName?.toLowerCase()?.includes('mortgage statement - first')
    )
  }, [])

  const { data: mortageStatementMetadata } = useGetDocumentMetadataQuery(mortageStatement?.uuid, {
    skip: !mortageStatement?.uuid,
  })

  const { data: liens } = useGetLiensQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const { data: payoffs } = useGetPayoffsQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const { data: valuations } = useGetValuationsByApplicationQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const { data: nonApplicants } = useListNonApplicantsQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const applicantProfile = getApplicationProfileByPosition(application?.uuid, 1)
  const coApplicantProfile = getApplicationProfileByPosition(application?.uuid, 2)
  const coApplicantProfile3 = getApplicationProfileByPosition(application?.uuid, 3)
  const coApplicantProfile4 = getApplicationProfileByPosition(application?.uuid, 4)
  const coApplicantProfile5 = getApplicationProfileByPosition(application?.uuid, 5)

  const hasForbearance = useMemo(() => {
    return !!documents?.find((doc) => doc.tag === 'Deal Files/Deferment-Forbearance Docs')
  }, [documents])

  const USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  const propertyType = useMemo(
    () =>
      application?.property?.propertyType
        ? `${
            propertyTypeOptions.find(
              (option) => option.value === application?.property?.propertyType
            )?.label
          }`
        : noContent,
    [application]
  )

  const hoa = yesOrNo(
    application?.property?.monthlyCostHoa && application?.property?.monthlyCostHoa > 0
  )

  const fia = useMemo(
    () =>
      applicationOfferCalcs?.selectedOffer?.offers?.offerInvestment
        ? applicationOfferCalcs?.selectedOffer?.offers?.offerInvestment ==
          applicationOfferCalcs?.maxOffer?.offers?.offerInvestment
          ? `${USDollar.format(
              applicationOfferCalcs?.selectedOffer?.offers?.offerInvestment
            )} (MAX)`
          : `${USDollar.format(applicationOfferCalcs?.selectedOffer?.offers?.offerInvestment)}`
        : noContent,
    [applicationOfferCalcs]
  )

  const avmApproved = useMemo(() => {
    const hasAvmValuation = valuations?.results?.some((valuation) => valuation.type == 'avm')

    return yesOrNo(
      (!hasAvmValuation && (overrides?.home_value || valuationsCalcs?.appraisalReport?.value)) ||
        atypicalityCheckCalcs?.automated_review_status === 'UAM Approved' ||
        atypicalityCheckCalcs?.automated_review_status === 'No Review Required'
    )
  }, [valuations, overrides, valuationsCalcs])

  const propertyUse = useMemo(
    () =>
      application?.property?.propertyUsage
        ? `${
            propertyUsageOptions.find(
              (option) => option.value === application?.property?.propertyUsage
            )?.label
          }`
        : noContent,
    [application]
  )

  const maritalStatus = useMemo(() => {
    const applicant = _find(applicationProfiles?.results, { position: 1 })

    return applicant?.profile?.marital_status
      ? maritalStatusOptions.find((status) => status.value === applicant?.profile?.marital_status)
          ?.label
      : noContent
  }, [applicationProfiles])

  const creditReports = useMemo(
    () =>
      sortedCreditReports?.length > 0
        ? sortedCreditReports
            ?.map(
              (report) =>
                `dated ${
                  report?.reportOrderedAt
                    ? format(new Date(report?.reportOrderedAt)?.setHours(24, 0, 0, 0), 'MM/dd/yyyy')
                    : noContent
                } valid to ${
                  report?.reportOrderedAt
                    ? format(
                        addDays(new Date(`${report?.reportOrderedAt?.slice(0, 10)} 00:00:00`), 90),
                        'MM/dd/yyyy'
                      )
                    : noContent
                }`
            )
            ?.join(', ')
        : noContent,
    [sortedCreditReports]
  )

  const ssnMatchesApp = useMemo(
    () =>
      yesOrNo(
        globalAudit?.['credit-report-xml']?.['audit_ssn'] === 'audited-match' ||
          globalAudit?.['credit-report-xml']?.['audit_ssn'] === 'not-required'
      ),
    [globalAudit]
  )

  const applicantsIds = useMemo(() => {
    const profiles = [].concat(
      applicantProfile?.profile ? [applicantProfile?.profile] : [],
      coApplicantProfile?.profile ? [coApplicantProfile?.profile] : [],
      coApplicantProfile3?.profile ? [coApplicantProfile3?.profile] : [],
      coApplicantProfile4?.profile ? [coApplicantProfile4?.profile] : [],
      coApplicantProfile5?.profile ? [coApplicantProfile5?.profile] : []
    )
    return (
      'valid to ' +
      (profiles?.length > 0
        ? profiles
            ?.map((profile) =>
              profile?.photo_id_expire
                ? format(new Date(profile?.photo_id_expire)?.setHours(24, 0, 0, 0), 'MM/dd/yyyy')
                : noContent
            )
            ?.join(' & ')
        : noContent)
    )
  }, [applicantProfile, coApplicantProfile, coApplicantProfile3, coApplicantProfile4])

  const coOwnersIds = useMemo(() => {
    const coOwners = nonApplicants?.results?.filter(
      (obj) => obj.profile_type === ProfileTypes.CO_OWNER
    )
    return (
      'valid to ' +
      (coOwners?.length > 0
        ? coOwners
            ?.map((coOwner) =>
              coOwner?.profile?.photo_id_expire
                ? format(
                    new Date(coOwner?.profile?.photo_id_expire)?.setHours(24, 0, 0, 0),
                    'MM/dd/yyyy'
                  )
                : noContent
            )
            ?.join(', ')
        : noContent)
    )
  }, [nonApplicants])

  const nooIds = useMemo(() => {
    const noos = nonApplicants?.results?.filter((obj) => obj.profile_type === ProfileTypes.NOO)
    return (
      'valid to ' +
      (noos?.length > 0
        ? noos
            ?.map((noo) =>
              noo?.profile?.photo_id_expire
                ? format(
                    new Date(noo?.profile?.photo_id_expire)?.setHours(24, 0, 0, 0),
                    'MM/dd/yyyy'
                  )
                : noContent
            )
            ?.join(', ')
        : noContent)
    )
  }, [nonApplicants])

  const hasPMTHistory = useMemo(
    () => documents?.find((doc) => doc.fileName.includes('Mortgage Payment History')),
    [documents]
  )

  const inquirityLOE = useMemo(
    () => yesOrNo(application?.atypicalityCheck?.isMtgInquires),
    [application]
  )

  const exceptionReview = useMemo(() => {
    const exceptions = escalations?.filter(
      (escalation) =>
        escalation.tab === 'exceptions' && !escalationFlagsTypes.includes(escalation.type)
    )

    const hasExceptions = exceptions?.length > 0

    const exceptionsList = exceptions?.map((exception) => exception.type_label)?.join(', ')

    return hasExceptions ? `${yesOrNo(hasExceptions)} - ${exceptionsList}` : yesOrNo(hasExceptions)
  }, [escalations])

  const flagsReview = useMemo(() => {
    const flags = escalations?.filter(
      (escalation) =>
        escalation.tab === 'exceptions' && escalationFlagsTypes.includes(escalation.type)
    )

    const hasflags = flags?.length > 0

    const flagsList = flags?.map((flag) => flag.type_label)?.join(', ')

    return hasflags ? `${yesOrNo(hasflags)} - ${flagsList}` : yesOrNo(hasflags)
  }, [escalations])

  const hoiReview = useMemo(
    () =>
      hoi && hoi?.results?.length > 0
        ? hoi?.results
            ?.map(
              (hoiItem) =>
                `valid to ${
                  hoiItem?.expiration_date
                    ? format(
                        new Date(hoiItem?.expiration_date)?.setHours(24, 0, 0, 0),
                        'MM/dd/yyyy'
                      )
                    : noContent
                }`
            )
            ?.join(' & ')
        : noContent,
    [hoi]
  )

  const replacementCost = useMemo(
    () =>
      hoi && hoi?.results?.length > 0
        ? hoi?.results
            ?.map((hotItem) =>
              hotItem?.replacement_cost
                ? `Yes → ${
                    hotItem?.replacement_cost_notes ? hotItem?.replacement_cost_notes : noContent
                  }`
                : 'No'
            )
            .join(', ')
        : noContent,
    [hoi]
  )

  const mortgageStatement = useMemo(
    () =>
      mortageStatementMetadata
        ? `dated ${
            mortageStatementMetadata?.statement_date
              ? mortageStatementMetadata?.statement_date
              : noContent
          }; HO past due: ${yesOrNo(
            mortageStatementMetadata?.total_monthly_due !==
              mortageStatementMetadata?.monthly_payment
          )}`
        : noContent,
    [mortageStatementMetadata]
  )

  const escrowed = useMemo(() => {
    const fstLien = liens?.results?.find((lien) => lien.lien_position === 1)

    return `${
      fstLien && fstLien?.escrow_status && fstLien?.escrow_status !== 'not-escrowed'
        ? `Yes - ${
            escrowStatusTypeOptions?.find((option) => fstLien?.escrow_status === option.value)
              ?.label
          }`
        : 'No'
    }`
  }, [liens])

  const appraisalReviewTemplates = useMemo(() => {
    const appraisalTypeReview = valuationsCalcs?.appraisalReport?.conditionOfAppraisalType
      ? conditionOfAppraisalTypeOptions?.find(
          (conditionType) =>
            conditionType.value === valuationsCalcs?.appraisalReport?.conditionOfAppraisalType
        )?.label
      : noContent

    const appraisedHomeValue = valuationsCalcs?.appraisalReport?.value
      ? USDollar.format(valuationsCalcs?.appraisalReport?.value)
      : undefined

    const appraisalReviewStatus = valuationsCalcs?.appraisalReport?.appraisalReviewStatus
      ? appraisalReviewStatusOptions?.find(
          (reviewStatus) =>
            reviewStatus.value === valuationsCalcs?.appraisalReport?.appraisalReviewStatus
        )?.label
      : undefined

    const appraisedHomeValueReview = appraisedHomeValue
      ? `${appraisedHomeValue} is ${appraisalReviewStatus ? appraisalReviewStatus : '~'}`
      : noContent

    const floodZoneReview =
      valuationsCalcs?.appraisalReport &&
      isBoolean(valuationsCalcs?.appraisalReport?.floodZoneHazardIndicator)
        ? yesOrNo(valuationsCalcs?.appraisalReport?.floodZoneHazardIndicator)
        : noContent

    const isHoa =
      valuationsCalcs?.appraisalReport && isBoolean(valuationsCalcs?.appraisalReport?.isHoa)
        ? yesOrNo(valuationsCalcs?.appraisalReport?.isHoa)
        : undefined

    const HoaMonthlyFee = application?.property?.monthlyCostHoa
      ? USDollar.format(application?.property?.monthlyCostHoa)
      : '$0.00'

    const hoaReview = isHoa
      ? isHoa === 'Yes'
        ? `${isHoa} - (${HoaMonthlyFee}/Month)`
        : isHoa
      : noContent

    const pudReview =
      valuationsCalcs?.appraisalReport && isBoolean(valuationsCalcs?.appraisalReport?.isPud)
        ? yesOrNo(valuationsCalcs?.appraisalReport?.isPud)
        : noContent

    const valuationsReview = `<ul>
        <li>Appraisal Type Reviewed: <strong>${appraisalTypeReview}</strong></li>
        <li>Appraised Home Value: <strong>${appraisedHomeValueReview}</strong></li>
        <li>Flood Zone: <strong>${floodZoneReview}</strong></li>
        <li>HOA: <strong>${hoaReview}</strong></li>
        <li>PUD: <strong>${pudReview}</strong></li>
        <li>Valuation Analyst has Notated/summarized final value reconcilliation and any additional requirements have been met (ex. flood ins., "subjected to" value has been addressed, other required changes have been provided in supplement report, etc): <strong>Yes</strong></li>
        </ul>`

    return {
      valuations_review: valuationsReview,
    }
  }, [application, notes])

  const finalApprovalUWReviewTemplate = useMemo(() => {
    const dscr = overrides?.dscr ? overrides?.dscr : noContent

    const applicationFico = applicationCalcs?.calcApplicationFico
      ? `${applicationCalcs?.calcApplicationFico} [Mid Score Matches Max/CR] -- FICO < 550 = (${
          applicationCalcs?.calcApplicationFico < 550 ? 'Yes' : 'No'
        })`
      : noContent

    const appraisalReviewStatus = valuationsCalcs?.appraisalReport?.appraisalReviewStatus
      ? appraisalReviewStatusOptions?.find(
          (reviewStatus) =>
            reviewStatus.value === valuationsCalcs?.appraisalReport?.appraisalReviewStatus
        )?.label
      : noContent

    const appraisalEffectiveDate = valuationsCalcs?.appraisalReport?.effectiveDate
      ? format(
          new Date(valuationsCalcs?.appraisalReport?.effectiveDate)?.setHours(24, 0, 0, 0),
          'MM/dd/yyyy'
        )
      : noContent

    const appraisalExpirationDate = valuationsCalcs?.appraisalReport?.effectiveDate
      ? format(
          addDays(
            new Date(valuationsCalcs?.appraisalReport?.effectiveDate).setHours(24, 0, 0, 0),
            90
          ),
          'MM/dd/yyyy'
        )
      : noContent

    const appraisal = `${appraisalReviewStatus}, Effective: ${appraisalEffectiveDate} and valid to ${appraisalExpirationDate}`

    const replacementCost =
      hoi && hoi?.results?.length > 0
        ? hoi?.results
            ?.map((hotItem) =>
              hotItem?.replacement_cost
                ? `Yes → ${
                    hotItem?.replacement_cost_notes ? hotItem?.replacement_cost_notes : noContent
                  }`
                : 'No'
            )
            .join(', ')
        : noContent

    const creditReports =
      sortedCreditReports?.length > 0
        ? sortedCreditReports
            ?.map(
              (report) =>
                `dated ${
                  report?.reportOrderedAt
                    ? format(new Date(report?.reportOrderedAt)?.setHours(24, 0, 0, 0), 'MM/dd/yyyy')
                    : noContent
                } valid to ${
                  report?.reportOrderedAt
                    ? format(
                        addDays(new Date(`${report?.reportOrderedAt?.slice(0, 10)} 00:00:00`), 90),
                        'MM/dd/yyyy'
                      )
                    : noContent
                }`
            )
            ?.join(', ')
        : noContent

    const loe = `${yesOrNo(
      application?.atypicalityCheck?.isMtgInquires ||
        documents?.find(
          (doc) =>
            doc?.fileName?.toLowerCase() === DOCUMENT_OPTIONS?.LOE_MORTGAGE_INQUIRIES?.toLowerCase()
        )
    )} - <span>enter details if applicable</span>`

    const mortgageStatement = mortageStatementMetadata
      ? `dated ${mortageStatementMetadata?.statement_date}; HO past due: ${
          mortageStatementMetadata?.total_monthly_due !== mortageStatementMetadata?.monthly_payment
            ? 'Yes'
            : 'No'
        }`
      : noContent

    const fstLien = liens?.results?.find((lien) => lien.lien_position === 1)

    const escrowed = `${
      fstLien && fstLien?.escrow_status && fstLien?.escrow_status !== 'not-escrowed'
        ? `Yes - ${
            escrowStatusTypeOptions?.find((option) => fstLien?.escrow_status === option.value)
              ?.label
          }`
        : 'No'
    }`

    const pmtHistory = hasPMTHistory ? '24 mos history uploaded and reviewed' : 'Not Required'

    const forbearance = `${yesOrNo(hasForbearance)} - enter details if applicable`

    const pr = application?.titleReport?.effectiveDate
      ? `${format(
          new Date(application?.titleReport?.effectiveDate)?.setHours(24, 0, 0, 0),
          'MM/dd/yyyy'
        )} valid to ${format(
          addDays(new Date(application?.titleReport?.effectiveDate).setHours(24, 0, 0, 0), 45),
          'MM/dd/yyyy'
        )}`
      : noContent

    const taxes = application?.titleReport?.taxes
      ? taxesOptions?.find((option) => option.value === application?.titleReport?.taxes)?.label
      : noContent

    const nextTaxDue = application?.titleReport?.nextTaxDue
      ? `${format(
          new Date(application?.titleReport?.nextTaxDue)?.setHours(24, 0, 0, 0),
          'MM/dd/yyyy'
        )}`
      : noContent

    const payoffsReview =
      payoffs?.results.length > 0
        ? payoffs?.results?.map(
            (payoff) =>
              `${payoff?.name} (${
                payoffTypeOptions?.find((option) => payoff?.payoff_type === option.value)?.label
              }) ${
                payoff?.good_through_on ? `${payoff?.good_through_on?.replace(/[()]/g, '')} ` : ''
              }${
                payoff?.payoff_amount
                  ? USDollar.format(parseFloat(payoff?.payoff_amount))
                  : noContent
              }`
          )
        : noContent

    const tsCommentary = 'enter details if applicable'

    const finalUWCommentary = `
    <ul>
      <li>FIA: <strong>${fia}</strong></li>
      <li>Property Use: <strong>${propertyUse}</strong></li>
      ${
        application?.property?.propertyUsage === 'investment'
          ? `<li>DSCR: <strong>${dscr}</strong></li>`
          : ''
      }
      <li>Property Type: <strong>${propertyType}</strong></li>
      <li>Application FICO: <strong>${applicationFico}</strong></li>
      <li>Exceptions: <strong>${exceptionReview}</strong></li>
      <li>Escalations/Flags: <strong>${flagsReview}</strong></li>
      <li>Appraisal: <strong>${appraisal}</strong></li>
      <li>HOA: <strong>${hoa}</strong></li>
      <li>HOI: <strong>${hoiReview}</strong></li>
      <li>Replacement Cost: <strong>${replacementCost}</strong></li>
      <li>Applicant ID(s): <strong>${applicantsIds}</strong></li>
      <li>Co-Owner ID(s): <strong>${coOwnersIds}</strong></li>
      <li>NOO ID(s):  <strong>${nooIds}</strong></li>
      <li>CR: <strong>${creditReports}</strong></li>
      <li>LOE: <strong>${loe}</strong></li>
      <li>Mortgage Statement: <strong>${mortgageStatement}</strong></li>
      <li>Escrowed: <strong>${escrowed}</strong></li>
      <li>PMT History: <strong>${pmtHistory}</strong></li>
      <li>Forbearance: <strong>${forbearance}</strong></li>
      <li>PR: <strong>${pr}</strong></li>
      <li>Taxes: <strong>${taxes}</strong></li>
      <li>Next Tax Due Date: <strong>${nextTaxDue}</strong></li>
      <li>POs: <strong>${payoffsReview}</strong></li>
      <li>TS Commentary: <strong>${tsCommentary}</strong></li>
    </ul>
    `
    return { final_uw_commentary: finalUWCommentary }
  }, [
    application,
    notes,
    applicationCalcs,
    applicationOfferCalcs,
    valuationsCalcs,
    escalations,
    overrides,
    hoi,
    sortedCreditReports,
    mortageStatementMetadata,
    liens,
    payoffs,
    hasForbearance,
    propertyType,
    hasPMTHistory,
    escrowed,
    mortgageStatement,
    creditReports,
    applicantsIds,
    coOwnersIds,
    nooIds,
    replacementCost,
    hoa,
    flagsReview,
    exceptionReview,
    propertyType,
    propertyUse,
    fia,
  ])

  const salesReviewTemplate = useMemo(() => {
    const onApplication = application?.profiles?.map((profile) => profile?.fullName)?.join(', ')

    const titleVesting = application?.property?.titleVesting
      ? application?.property?.titleVesting
      : noContent

    const useOfFunds =
      useOfProceeds && useOfProceeds.length
        ? useOfProceeds
            ?.map(
              (useOfProceed) =>
                useOfProceedsOptions.find((option) => option.value === useOfProceed.name).label
            )
            .join(', ')
        : noContent

    const firstMtg = _find(liens?.results, { lien_position: 1 })
    const firstMtgName = firstMtg ? firstMtg?.name : noContent

    const secondMtg = _find(liens?.results, { lien_position: 2 })
    const secondMtgName = secondMtg ? secondMtg?.name : noContent

    const bkFcReview = yesOrNo(
      hasForbearance ||
        checkWithinThreshold(
          applicationCalcs?.lastBankruptcy,
          realCreditReport?.reportOrderedAt,
          5
        ) === 'within 5 years'
    )

    const solar = isBoolean(application?.property?.roofSolarPanel)
      ? yesOrNo(application?.property?.roofSolarPanel)
      : noContent

    const salesReviewTemplate = `
    <ul>
      <li>On Application: <strong>${onApplication}</strong></li>
      <li>On Title: <strong>${titleVesting}</strong></li>
      <li>Marital Status: <strong>${maritalStatus}</strong></li>
      <li>Property Type: <strong>${propertyType}</strong></li>
      <li>Use of Funds: <strong>${useOfFunds}</strong></li>
      <li>1st MTG: <strong>${firstMtgName}</strong></li>
      <li>2nd MTG: <strong>${secondMtgName}</strong></li>
      <li>Def Balance: <strong>enter details if applicable</strong></li>
      <li>Balance Match CR: <strong>enter details if applicable</strong></li>
      <li>BK/FC: <strong>${bkFcReview}</strong></li>
      <li>SSN Matches App: <strong>${ssnMatchesApp}</strong></li>
      <li>DOB Matches App: <strong>required</strong></li>
      <li>HOI: <strong>${hoiReview}</strong></li>
      <li>HOA: <strong>${hoa}</strong></li>
      <li>inquirity LOE: <strong>${inquirityLOE}</strong></li>
      <li>SOLAR: <strong>${solar}</strong></li>
      <li>AVM Approved: <strong>${avmApproved}</strong></li>
      <li>Signed Docs: <strong>enter details if applicable</strong></li>
      <li>Exceptions: <strong>${exceptionReview}</strong></li>
    </ul>
    `
    return {
      sales_commentary: salesReviewTemplate,
    }
  }, [
    application,
    propertyType,
    useOfProceeds,
    liens,
    hasForbearance,
    realCreditReport,
    maritalStatus,
    ssnMatchesApp,
    hoiReview,
    hoa,
    inquirityLOE,
    avmApproved,
    exceptionReview,
  ])

  const initialUWReviewTemplate = useMemo(() => {
    const hasCoo = yesOrNo(
      nonApplicants?.results?.some((nonApp) => nonApp?.profile_type === ProfileTypes.CO_OWNER)
    )

    const hasNoo = yesOrNo(
      nonApplicants?.results?.some((nonApp) => nonApp?.profile_type === ProfileTypes.NOO)
    )

    const applicationFico = applicationCalcs?.calcApplicationFico
      ? applicationCalcs?.calcApplicationFico
      : noContent

    const hasBankruptcy = yesOrNo(
      checkWithinThreshold(
        applicationCalcs?.lastBankruptcy,
        realCreditReport?.reportOrderedAt,
        5
      ) === 'within 5 years'
    )

    const currentDate = new Date()
    const twentyFourMonthsAgo = new Date(currentDate)
    twentyFourMonthsAgo.setMonth(currentDate.getMonth() - 24)

    const hasRecentLiens = !!liens?.results?.find(
      (lien) => lien?.account_opened_on && new Date(lien?.account_opened_on) > twentyFourMonthsAgo
    )

    const sufficientMtgHistory = yesOrNo(hasPMTHistory || hasRecentLiens)

    const template = `
    <ul>
    <li>FIA: <strong>${fia}</strong></li>
    <li>AVM Approved: <strong>${avmApproved}</strong></li>
    <li>Property Use: <strong>${propertyUse}</strong></li>
    <li>Property Type: <strong>${propertyType}</strong></li>
    <li>Applicant Marital Status: <strong>${maritalStatus}</strong></li>
    <li>Co-Owner: <strong>${hasCoo}</strong></li>
    <li>NOO: <strong>${hasNoo}</strong></li>
    <li>Application FICO: <strong>${applicationFico}</strong></li>
    <li>CR: <strong>${creditReports}</strong></li>
    <li>SSN Matches App: <strong>${ssnMatchesApp}</strong></li>
    <li>Applicant ID(s): <strong>${applicantsIds}</strong></li>
    <li>Co-Owner ID(s): <strong>${coOwnersIds}</strong></li>
    <li>NOO ID(s):  <strong>${nooIds}</strong></li>
    <li>DOB Matches App: <strong>required</strong></li>
    <li>BK - 5 YRS: <strong>${hasBankruptcy}</strong></li>
    <li>FB/Mod: <strong>${yesOrNo(hasForbearance)}</strong></li>
    <li>Sufficient Mtg History: <strong>${sufficientMtgHistory}</strong></li>
    <li>inquiry LOE: <strong>${inquirityLOE}</strong></li>
    <li>Exceptions: <strong>${exceptionReview}</strong></li>
    <li>Escalations/Flags: <strong>${flagsReview}</strong></li>
    <li>HOA: <strong>${hoa}</strong></li>
    <li>HOI: <strong>${hoiReview}</strong></li>
    <li>Replacement Cost: <strong>${replacementCost}</strong></li>
    <li>Mortgage Statement: <strong>${mortgageStatement}</strong></li>
    <li>Escrowed: <strong>${escrowed}</strong></li>
    <li>TS Commentary: <strong>enter detail if applicable</strong></li>
  </ul>
    `
    return {
      initial_uw_commentary: template,
    }
  }, [
    fia,
    avmApproved,
    propertyUse,
    propertyType,
    maritalStatus,
    nonApplicants,
    creditReports,
    ssnMatchesApp,
    applicantsIds,
    coOwnersIds,
    nooIds,
    applicationCalcs,
    realCreditReport,
    hasForbearance,
    inquirityLOE,
    exceptionReview,
    flagsReview,
    hoa,
    hoiReview,
    replacementCost,
    mortgageStatement,
    escrowed,
  ])

  return {
    templates: {
      ...appraisalReviewTemplates,
      ...finalApprovalUWReviewTemplate,
      ...salesReviewTemplate,
      ...initialUWReviewTemplate,
    },
  }
}
